import { Grid } from '@material-ui/core';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useSelector } from 'react-redux';
import BaseButton from 'src/common/components/base-button';
import { setHomePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import CashierPreferencesHelper from 'src/common/helpers/cashier-preferences-helper';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import TrainingModeWatermark from 'src/common/training-mode/training-mode-watermark';
import { hasCardData } from 'src/devices/payment/get-card-details';
import { getSignatureFromPinPad } from 'src/devices/payment/pinpad-operations';
import { getNotificationReSign } from 'src/tendering/business-logic/handle-signature-selection';
import TenderProcessingTitle from 'src/tendering/components/process-tender/tender-processing-title';
import TenderAmountDue from '../tender-amount-due';
import { styles } from './checkout-signature-styles';

type Props = CheckoutSignatureProps & SignatureProps;

export const showTenderSignatureView = (signatureImageData?: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const signatureProps: SignatureProps = {
      onApprove: resolve,
      onReject: reject,
      signatureImageData,
    };
    setHomePage(<CheckoutSignature {...signatureProps} />, undefined, { isReplaceablePage: true });
  });

const CheckoutSignaturePage = ({
  isProcessingSignature,
  signatureImageData,
  amountDue,
  tenderTitle,
  onApprove,
  onReject,
}: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const isLeftHanded = CashierPreferencesHelper.isLeftHanded();
  const classes = styles();
  const trainingClasses = trainingModeStyles();

  const [info, setInfo] = useState<string>(t('EFTSignature_ApproveReject'));
  const [image, setImage] = useState<string>('');
  const [signatureDenyCount, setSignatureDenyCount] = useState<number>(0);

  const paymentTranID = useSelector((state: AppState) => {
    return state.pinPad.paymentTransactionId || '';
  });

  const trainingModeActive = useSelector((state: AppState): boolean => state.posMode.trainingMode);

  useEffect((): void => {
    setImage(signatureImageData ?? '');
  }, [signatureImageData]);

  if (!isProcessingSignature) {
    return null;
  }

  const waitForSignatureFlow = async (): Promise<void> => {
    setSignatureDenyCount(signatureDenyCount + 1);
    await getNotificationReSign();
    setInfo(t('EPS_DefaultMessageForCashierDisplay'));
    setImage('');
    if (hasCardData()) {
      const details = await getSignatureFromPinPad(paymentTranID);
      setImage(details.imageData || '');
      setInfo(t('EFTSignature_ApproveReject'));
    }
  };

  return (
    <Grid
      item
      container
      direction='row'
      justifyContent='space-between'
      className={clsx('fullHeight', 'flexFull', classes.viewContainer, {
        [trainingClasses.trainingModeBackground]: trainingModeActive,
      })}
    >
      {trainingModeActive && (
        <TrainingModeWatermark
          supplementalStyles={trainingClasses.trainingWaterMark}
          dataTestId='action-window-training-mode-watermark'
        />
      )}

      <Grid
        item
        container
        direction='row'
        justifyContent='space-between'
        className={classes.viewHeader}
      >
        <Grid item lg={9}>
          <TenderProcessingTitle title={tenderTitle} />
        </Grid>
        <Grid item>
          <TenderAmountDue amountDue={amountDue} align='right' />
        </Grid>
      </Grid>

      {!isLeftHanded && (
        <Grid item container direction='row' className={classes.rightHandedContent}>
          <CenterMessagingColumn signatureInfo={info} signatureImageData={image} />
        </Grid>
      )}

      {isLeftHanded && (
        <Grid item container direction='row' className={classes.leftHandedContent}>
          <CenterMessagingColumn signatureInfo={info} signatureImageData={image} />
        </Grid>
      )}
      <Grid item container direction='row' alignContent={'flex-end'}>
        <Grid item container direction='row' className={classes.approveButtonContainer}>
          <BaseButton
            buttonType='white'
            className={classes.rejectButton}
            onClick={async () => {
              if (signatureDenyCount < 2) {
                await waitForSignatureFlow();
              } else {
                setSignatureDenyCount(0);
                onReject();
                setInfo(t('EPS_DefaultMessageForCashierDisplay'));
              }
            }}
            data-testid='signature-reject'
          >
            {t('Reject')}
          </BaseButton>
          <BaseButton
            buttonType='primary'
            className={classes.approveButton}
            onClick={onApprove}
            data-testid='signature-approve'
          >
            {t('Approve')}
          </BaseButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

interface CheckoutSignatureProps {
  isProcessingSignature: boolean;
  total: number;
  isKeypadVisible: boolean;
  amountDue: number;
  tenderTitle: string;
}

export interface SignatureProps {
  signatureImageData?: string;
  onApprove: () => void;
  onReject: () => void;
}

const mapStateToProps = (state: AppState): CheckoutSignatureProps => ({
  isProcessingSignature: state.tendering.isProcessingSignature,
  total: state.itemList.total,
  isKeypadVisible: state.middleArea.salesKeypad,
  amountDue: state.itemList.amountDue,
  tenderTitle: state.tenderMenu.selectedTenderGroup?.name || '',
});

const CheckoutSignature = connect(mapStateToProps)(CheckoutSignaturePage);
export default CheckoutSignature;

interface CenterMessagingColumnProps {
  signatureInfo: string;
  signatureImageData: string;
}

const CenterMessagingColumn = ({
  signatureInfo,
  signatureImageData,
}: CenterMessagingColumnProps): JSX.Element => {
  const classes = styles();

  return (
    <Grid item container md={7} direction='column' className={classes.centerMessaging}>
      <Grid
        data-testid='signature-info'
        item
        className={`${classes.paymentInfo} ${classes.textAlignCenter}`}
      >
        {signatureInfo}
        <Grid
          data-testid='signature-data'
          item
          className={`${classes.paymentInfo} ${classes.alignCenterImage}`}
        >
          {signatureImageData ? (
            <img alt='Signature' src={`data:image/png;base64,${signatureImageData}`} />
          ) : (
            ''
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
